/**
 * /manage/connections — connect external drive/email services.
 *
 * Org-scoped list of Connections (`GET /connections`) with connect + disconnect.
 * "Connect a service" is a two-step wizard: pick a provider from a grid
 * (`GET /connections/providers`), then either sign in (deployment-owned OAuth
 * apps — the provider reports `deployment_managed_auth`, so we show a sign-in
 * callout and the user fills in nothing) or complete a form built from the
 * provider's `config_schema` (+ credential fields for api_key/basic).
 *
 * OAuth (connect AND reconnect) runs in a popup while this window waits: the
 * callback posts its result back and the popup closes. Closing the popup instead
 * abandons the flow — a fresh connect deletes its half-made row, and a reconnect
 * leaves the working connection exactly as it was.
 *
 * Each row shows only what identifies the connection (account, person, what it
 * reaches); scopes and app/config detail sit behind a per-row accordion.
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { formatDistanceToNow } from "date-fns";
import { Clock, Plus, RotateCw, ShieldCheck, Trash2, TriangleAlert } from "lucide-react";
import { useEffect, useState } from "react";
import { toast } from "sonner";

import { useApi } from "@/api";
import { PageHeader } from "@/components/manage/PageHeader";
import { Section } from "@/components/manage/Section";
import { StatStrip } from "@/components/manage/StatStrip";
import { StatusBadge } from "@/components/manage/StatusBadge";
import {
  type AuthStartOut,
  type ConnectionCreateResult,
  type ConnectionOut,
  ProviderIcon,
  type ProviderOut,
  type SurfaceOut,
  configItems,
  connectionStatusTone,
  isAuthStart,
  permissionItems,
  runOAuthPopup,
} from "@/components/manage/connectors";
import { useActiveOrgId, useIsDemoManage } from "@/components/manage/org-context";
import { signInNotice } from "@/components/manage/signIn";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import { Spinner } from "@/components/ui/spinner";

export const Route = createFileRoute("/manage/connections")({
  component: ConnectionsPage,
});

const isOAuthKind = (k: string) => k === "oauth2" || k === "oauth2_pkce";

/** The create-only write ports, and what each one lets Chaidojo do (F6). */
const WRITE_PORTS: Record<string, string> = {
  file_sink: "can add files",
  mail_sink: "can draft mail",
  mail_send: "can send mail",
};

/** What this connection lets the platform WRITE, in a phrase — or "read-only",
 *  which is the honest default and the state of every connection made before
 *  write-back existed. Never a warning: read-only is the safe state, not a
 *  fault, and a person who wants more grants it. */
function writeSummary(c: ConnectionOut): string {
  const ports = (c.write_ports ?? []).map((p) => WRITE_PORTS[p]).filter(Boolean);
  return ports.length ? ports.join(", ") : "read-only";
}

/** Surfaces this connection could still be granted that would let it WRITE.
 *  What "Reconnect with write access" asks for, and why the button appears. */
function missingWriteSurfaces(c: ConnectionOut): SurfaceOut[] {
  return (c.available_surfaces ?? []).filter(
    (s) => s.port === "file_sink" || s.port === "mail_sink",
  );
}

/** What this connection actually reaches: the bucket/container/root for object
 *  stores (which is the whole grant), or the surfaces consented to for
 *  account-based providers. The mailbox is the account's own, so the headline
 *  email is also the inbox address. */
function accessSummary(c: ConnectionOut): string | null {
  const cfg = c.config_summary ?? {};
  for (const key of ["bucket", "container", "root", "path"]) {
    const v = cfg[key];
    if (typeof v === "string" && v) return v;
  }
  const parts = [
    ...(c.ports.includes("file_source") ? ["Drive"] : []),
    ...(c.ports.includes("mailbox_source") ? ["Inbox"] : []),
  ];
  return parts.length ? parts.join(" + ") : null;
}

function ConnectionsPage() {
  const api = useApi();
  const orgId = useActiveOrgId();
  const qc = useQueryClient();
  // Demo sessions have no connectors surface at all — the API 403s every call
  // and the nav hides this page; a typed-in URL gets a dead end, not a pitch.
  const isDemo = useIsDemoManage();
  // The connection whose re-consent popup is open; its row shows a loader.
  const [reauthing, setReauthing] = useState<string | null>(null);

  const providers = useQuery({
    queryKey: ["connection-providers"],
    queryFn: () => api.request<ProviderOut[]>("/connections/providers"),
    enabled: !isDemo,
  });

  const connections = useQuery({
    queryKey: ["connections", orgId],
    enabled: !isDemo,
    queryFn: () => api.request<ConnectionOut[]>("/connections"),
  });

  const remove = useMutation({
    mutationFn: (id: string) =>
      api.request<void>(`/connections/${id}`, { method: "DELETE" }, false),
    // Optimistically drop the row so it disappears immediately (no refresh needed),
    // then reconcile with the server.
    onMutate: async (id: string) => {
      await qc.cancelQueries({ queryKey: ["connections"] });
      const prev = qc.getQueryData<ConnectionOut[]>(["connections", orgId]);
      qc.setQueryData<ConnectionOut[]>(["connections", orgId], (old) =>
        (old ?? []).filter((c) => c.id !== id),
      );
      return { prev };
    },
    onError: (err: { message?: string }, _id, ctx) => {
      if (ctx?.prev) qc.setQueryData(["connections", orgId], ctx.prev);
      toast.error(err.message ?? "Disconnect failed");
    },
    onSuccess: () => {
      toast.success("Disconnected");
    },
    onSettled: () => {
      // Prefix invalidation (not keyed on orgId) so it always refetches, and
      // refresh the source chips on the datasets list too.
      void qc.invalidateQueries({ queryKey: ["connections"] });
      void qc.invalidateQueries({ queryKey: ["connector-sources"] });
    },
  });

  // Popup blocked → the flow fell back to a full-page redirect and the callback
  // sent us back here with a flag. Surface it, refresh, and strip the query.
  useEffect(() => {
    const sp = new URLSearchParams(window.location.search);
    const ok = sp.get("connected");
    const err = sp.get("connect_error");
    if (!ok && !err) return;
    if (ok) {
      toast.success("Connected");
      void qc.invalidateQueries({ queryKey: ["connections"] });
      void qc.invalidateQueries({ queryKey: ["connector-sources"] });
    } else if (err) {
      toast.error(err);
    }
    window.history.replaceState({}, "", window.location.pathname);
  }, [qc]);

  /** Re-run consent for an existing connection: restore access it lost, or widen
   *  it ("all" = every surface the provider offers, i.e. Add access). Nothing
   *  changes until the callback completes, so abandoning the popup is free. */
  async function reconnect(c: ConnectionOut, widen?: "all") {
    setReauthing(c.id);
    try {
      const surfaces =
        widen === "all"
          ? [...(c.granted_surfaces ?? []), ...(c.available_surfaces ?? [])].map((s) => s.id)
          : undefined;
      const start = await api.request<AuthStartOut>(`/connections/${c.id}/reauth`, {
        method: "POST",
        body: JSON.stringify({ surfaces: surfaces ?? null }),
      });
      if (!start.redirect_url) {
        toast.error("Re-consent isn't available for this connection.");
        return;
      }
      const res = await runOAuthPopup(start.redirect_url);
      if (!res) return; // abandoned — existing credentials keep working
      if (res.ok) {
        toast.success(res.message || "Reconnected");
        void qc.invalidateQueries({ queryKey: ["connections"] });
      } else if (res.already) {
        toast.info(res.message);
      } else {
        toast.error(res.message || "Reconnect failed");
      }
    } catch (err) {
      toast.error((err as { message?: string }).message ?? "Reconnect failed");
    } finally {
      setReauthing(null);
    }
  }

  const total = connections.data?.length ?? 0;
  const connected = connections.data?.filter((c) => c.status === "connected").length ?? 0;
  const totalSources = connections.data?.reduce((acc, c) => acc + c.source_count, 0) ?? 0;

  if (isDemo) {
    return (
      <div className="mx-auto max-w-[960px] px-10 py-10 text-sm text-muted-foreground">
        Not available in this demo.
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-[960px] px-10 py-10">
      <PageHeader
        eyebrow="Admin · Data"
        title="Connections"
        description="Connect a drive or mailbox once, then sync its folders into datasets. Files are read through the provider — ChaiDojo never stores your credentials in the clear."
        actions={<ConnectServiceDialog providers={providers.data ?? []} />}
      />

      <StatStrip
        items={[
          { label: "Connections", value: total, hint: "services linked" },
          { label: "Connected", value: connected, hint: "healthy" },
          { label: "Sources", value: totalSources, hint: "synced folders" },
          {
            label: "Providers",
            value: providers.data?.length ?? 0,
            hint: "available",
          },
        ]}
      />

      <Section title="Connected services">
        {connections.isLoading ? (
          <Skeleton className="h-32 w-full rounded-md" />
        ) : connections.data?.length ? (
          <div className="flex flex-col gap-2">
            {connections.data.map((c) => {
              const perms = permissionItems(c.provider, c.scopes);
              const cfg = configItems(c.config_summary);
              const isErr = c.status === "error" || c.status === "degraded";
              const canAdd = c.available_surfaces ?? [];
              const missingWrite = missingWriteSurfaces(c);
              // A connection with NO write port is the state the sink's refusal
              // points at ("reconnect it with write access"), so when write is
              // among what is still on offer, the button says that rather than
              // the generic "Add access" — which is true but tells a person
              // nothing about what they are being asked to hand over. Once it
              // HAS write, anything else on offer is ordinary read access again.
              const writeOnly = (c.write_ports ?? []).length === 0 && missingWrite.length > 0;
              const waiting = reauthing === c.id;
              // Google ends a Testing app's sign-ins after 7 days; from a day
              // before, Reconnect starts a new 7 (next wave A8).
              const signIn = signInNotice(c);
              const renew = !isErr && !!signIn?.reconnect;
              // Prefer naming the surfaces actually granted ("OneDrive + Outlook
              // email") over a generic guess — it's the honest answer to "what did
              // I hand over?". Object stores have no surfaces, so show the path.
              const access = c.granted_surfaces?.length
                ? c.granted_surfaces.map((s) => s.label).join(" + ")
                : accessSummary(c);
              const meta = [
                c.account_name,
                c.display_name,
                access,
                // What Chaidojo may WRITE here — the other half of "what did I
                // hand over?", and the one the write policy turns on.
                writeSummary(c),
                `${c.source_count} ${c.source_count === 1 ? "source" : "sources"}`,
                // "Sign-in expires around 29 Sep, 05:52" — Google's 7 days for
                // an app in testing, said while there is still time.
                signIn?.tone === "info" ? signIn.text : null,
              ].filter(Boolean) as string[];
              return (
                <div
                  key={c.id}
                  className="rounded-lg border border-[var(--color-hairline)] bg-card"
                >
                  <div className="flex items-center gap-3 px-4 py-3">
                    <div className="flex size-9 shrink-0 items-center justify-center rounded-md bg-[var(--color-surface-soft)]">
                      <ProviderIcon provider={c.provider} />
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2">
                        <span className="truncate font-medium text-foreground">
                          {c.account_label ?? c.display_name}
                        </span>
                        <StatusBadge label={c.status} tone={connectionStatusTone(c.status)} />
                      </div>
                      <p className="mt-0.5 truncate text-xs text-muted-foreground">
                        {meta.join(" · ")}
                        {c.created_at && (
                          <>
                            {" "}
                            · added{" "}
                            {formatDistanceToNow(new Date(c.created_at), { addSuffix: true })}
                          </>
                        )}
                      </p>
                    </div>
                    <div className="flex shrink-0 items-center gap-2">
                      {/* Never offered for a healthy connection that already holds
                          everything: there'd be nothing to re-consent and signing
                          in again is only a chance to land on the wrong account. */}
                      {isOAuthKind(c.auth_kind) && (isErr || renew || canAdd.length > 0) && (
                        <Button
                          variant="outline"
                          size="sm"
                          disabled={waiting}
                          onClick={() => void reconnect(c, isErr || renew ? undefined : "all")}
                          title={
                            isErr
                              ? `Sign in to ${c.account_label ?? c.display_name} again to restore access`
                              : renew
                                ? `Sign in to ${c.account_label ?? c.display_name} again before Google ends this sign-in`
                              : writeOnly
                                ? `Let Chaidojo ADD new files and drafts here (${missingWrite
                                    .map((s) => s.label)
                                    .join(", ")}). It never edits or deletes what is already there.`
                                : `Also give Chaidojo access to ${canAdd.map((s) => s.label).join(", ")}`
                          }
                        >
                          {waiting ? (
                            <>
                              <Spinner className="size-3.5" />
                              Waiting…
                            </>
                          ) : (
                            <>
                              <RotateCw className="size-3.5" />
                              {isErr || renew
                                ? "Reconnect"
                                : writeOnly
                                  ? "Reconnect with write access"
                                  : "Add access"}
                            </>
                          )}
                        </Button>
                      )}
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          if (
                            confirm(
                              `Disconnect ${c.account_label ?? c.display_name}? This removes its synced sources.`,
                            )
                          ) {
                            remove.mutate(c.id);
                          }
                        }}
                      >
                        <Trash2 className="size-3.5" />
                        Disconnect
                      </Button>
                    </div>
                  </div>

                  {/* The day-before (or after) warning. The plain date rides the
                      meta line above; only a sign-in about to end takes a row. */}
                  {!isErr && signIn && signIn.tone !== "info" && (
                    <div className="mx-4 mb-3 flex items-start gap-1.5 rounded-md bg-[var(--color-card-tint-rose)] px-2.5 py-1.5 text-xs text-foreground">
                      <Clock className="mt-0.5 size-3.5 shrink-0 text-amber-600" />
                      <span>
                        {signIn.text} Google ends sign-ins after 7 days while the Chaidojo app
                        is in testing; signing in again starts a new 7.
                      </span>
                    </div>
                  )}

                  {isErr ? (
                    <div className="mx-4 mb-3 flex items-start gap-1.5 rounded-md bg-[var(--color-card-tint-rose)] px-2.5 py-1.5 text-xs text-foreground">
                      <TriangleAlert className="mt-0.5 size-3.5 shrink-0 text-amber-600" />
                      <span>
                        {c.last_error ??
                          (c.status === "degraded"
                            ? "Sign-in didn't finish — reconnect this service, or remove it."
                            : "This connection has an error.")}
                      </span>
                    </div>
                  ) : canAdd.length > 0 ? (
                    <div className="mx-4 mb-3 flex items-start gap-1.5 rounded-md bg-[var(--color-surface-soft)] px-2.5 py-1.5 text-xs text-muted-foreground">
                      <ShieldCheck className="mt-0.5 size-3.5 shrink-0" />
                      <span>
                        This connection can also read{" "}
                        <strong className="font-medium text-foreground">
                          {canAdd.map((s) => s.label).join(" and ")}
                        </strong>{" "}
                        — add access to enable it. What it already reaches keeps working either way.
                      </span>
                    </div>
                  ) : null}

                  {/* Scopes + app/config detail: useful when auditing, noise the
                      rest of the time — so it starts collapsed. */}
                  <Accordion className="border-t border-[var(--color-hairline-soft)]">
                    <AccordionItem value="details" className="border-b-0">
                      <AccordionTrigger className="px-4 text-xs font-normal text-muted-foreground hover:no-underline">
                        Permissions &amp; details
                      </AccordionTrigger>
                      <AccordionContent className="px-4">
                        <div className="grid gap-4 sm:grid-cols-2">
                          <div>
                            <div className="mb-1.5 flex items-center gap-1.5 text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
                              <ShieldCheck className="size-3.5" />
                              Permissions granted
                            </div>
                            <ul className="flex flex-col gap-1">
                              {perms.map((p) => (
                                <li key={p.raw} className="text-xs text-foreground">
                                  {p.label}
                                  {p.raw !== p.label &&
                                    p.raw !== "none" &&
                                    p.raw !== "local filesystem" && (
                                      <span className="ml-1 font-mono text-[10px] text-muted-foreground">
                                        {p.raw}
                                      </span>
                                    )}
                                </li>
                              ))}
                            </ul>
                          </div>

                          <div>
                            <div className="mb-1.5 text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
                              Details
                            </div>
                            <dl className="flex flex-col gap-1 text-xs">
                              {cfg.map((it) => (
                                <div key={it.label} className="flex gap-2">
                                  <dt className="shrink-0 text-muted-foreground">{it.label}</dt>
                                  <dd
                                    className="min-w-0 truncate font-mono text-foreground"
                                    title={it.value}
                                  >
                                    {it.value}
                                  </dd>
                                </div>
                              ))}
                              <div className="flex gap-2">
                                <dt className="shrink-0 text-muted-foreground">Scope</dt>
                                <dd className="text-foreground">
                                  {c.workspace_id ? "This workspace" : "Org-wide"}
                                </dd>
                              </div>
                            </dl>
                          </div>
                        </div>
                      </AccordionContent>
                    </AccordionItem>
                  </Accordion>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="rounded-lg border border-dashed border-[var(--color-hairline)] bg-card px-4 py-10 text-center text-sm text-muted-foreground">
            No connections yet — connect a service to sync folders into datasets.
          </div>
        )}
      </Section>
    </div>
  );
}

type WizardStep = "pick" | "configure";

function ConnectServiceDialog({ providers }: { providers: ProviderOut[] }) {
  const api = useApi();
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [step, setStep] = useState<WizardStep>("pick");
  const [providerId, setProviderId] = useState("");
  const [label, setLabel] = useState("");
  const [config, setConfig] = useState<Record<string, string>>({});
  const [credentials, setCredentials] = useState<Record<string, string>>({});
  const [submitting, setSubmitting] = useState(false);
  // True once the provider's popup is open and we're waiting on the user.
  const [waitingOnAuth, setWaitingOnAuth] = useState(false);
  // Which surfaces to ask for. Defaults to all — the common case — but every one
  // can be unticked, so nobody grants a mailbox to sync a folder.
  const [chosen, setChosen] = useState<string[]>([]);

  const provider = providers.find((p) => p.id === providerId);
  const surfaces = provider?.surfaces ?? [];
  const props = provider?.config_schema.properties ?? {};
  const requiredKeys = provider?.config_schema.required ?? [];
  const authKind = provider?.auth_kind ?? "none";
  const isOAuth = isOAuthKind(authKind);
  // Deployment owns the OAuth app → the user just signs in; the config form
  // (app creds) is stripped server-side, so we show a sign-in callout instead.
  const deploymentManaged = Boolean(provider?.deployment_managed_auth);

  function reset() {
    setStep("pick");
    setProviderId("");
    setLabel("");
    setConfig({});
    setCredentials({});
    setChosen([]);
    setWaitingOnAuth(false);
  }

  function pickProvider(id: string) {
    setProviderId(id);
    setConfig({});
    setCredentials({});
    setLabel("");
    // Everything on by default — least privilege is available, not imposed.
    setChosen((providers.find((p) => p.id === id)?.surfaces ?? []).map((s) => s.id));
    setStep("configure");
  }

  function toggleSurface(id: string) {
    setChosen((c) => (c.includes(id) ? c.filter((x) => x !== id) : [...c, id]));
  }

  function done() {
    void qc.invalidateQueries({ queryKey: ["connections"] });
    void qc.invalidateQueries({ queryKey: ["connector-sources"] });
    setOpen(false);
    reset();
  }

  async function submit() {
    setSubmitting(true);
    try {
      const res = await api.request<ConnectionCreateResult>("/connections", {
        method: "POST",
        body: JSON.stringify({
          // A tile may be a variant of a shared adapter (Microsoft work vs
          // personal), so connect with its underlying provider and pass the
          // tile's audience through config.
          provider: provider?.provider || providerId,
          config: {
            ...config,
            ...(provider?.account_type ? { account_type: provider.account_type } : {}),
          },
          account_label: label || null,
          credentials,
          // Only what was ticked is requested; null lets indivisible providers
          // (a bucket) keep their all-or-nothing behaviour.
          surfaces: surfaces.length ? chosen : null,
        }),
      });

      if (!isAuthStart(res)) {
        toast.success("Connected");
        done();
        return;
      }
      if (!res.redirect_url) {
        toast.error("Provider requires an auth step that isn't available yet.");
        return;
      }

      // Hand the sign-in to a popup and wait here.
      setWaitingOnAuth(true);
      const out = await runOAuthPopup(res.redirect_url);
      if (!out) {
        // Abandoned: drop the half-made row so it never shows up in the list.
        if (res.connection_id) {
          await api
            .request<void>(`/connections/${res.connection_id}`, { method: "DELETE" }, false)
            .catch(() => {});
        }
        setWaitingOnAuth(false);
        return;
      }
      if (out.ok) {
        toast.success(out.message || "Connected");
        done();
      } else if (out.already) {
        // Same account as an existing connection — nothing was added.
        toast.info(out.message);
        done();
      } else {
        toast.error(out.message || "Connect failed");
        setWaitingOnAuth(false);
      }
    } catch (err) {
      toast.error((err as { message?: string }).message ?? "Connect failed");
      setWaitingOnAuth(false);
    } finally {
      setSubmitting(false);
    }
  }

  const missingConfig = requiredKeys.some((k) => !config[k]?.trim());
  const missingCreds =
    (authKind === "api_key" && !credentials.api_key?.trim()) ||
    (authKind === "basic" && (!credentials.username?.trim() || !credentials.password?.trim()));
  const canSubmit =
    Boolean(providerId) &&
    !missingConfig &&
    !missingCreds &&
    (surfaces.length === 0 || chosen.length > 0);

  const submitLabel = submitting
    ? "Connecting…"
    : deploymentManaged
      ? `Sign in with ${provider?.display_name}`
      : isOAuth
        ? "Continue"
        : "Connect";

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => {
        // Don't let a click-away drop the dialog while a popup is mid-flight.
        if (!o && waitingOnAuth) return;
        setOpen(o);
        if (!o) reset();
      }}
    >
      <DialogTrigger
        render={
          <Button variant="brand">
            <Plus className="size-4" />
            Connect a service
          </Button>
        }
      />
      <DialogContent>
        <DialogHeader>
          {step === "pick" ? (
            <>
              <DialogTitle>Connect a service</DialogTitle>
              <DialogDescription>
                Choose a service to connect. One connection covers everything it offers — Microsoft
                365 and Google Workspace bring both drive and email; you pick files or mail later,
                per dataset.
              </DialogDescription>
            </>
          ) : (
            <>
              <DialogTitle className="flex items-center gap-2">
                <ProviderIcon provider={providerId} className="size-5" />
                Connect {provider?.display_name}
              </DialogTitle>
              <DialogDescription>
                {waitingOnAuth
                  ? `Finish signing in to ${provider?.display_name} in the window that opened.`
                  : deploymentManaged
                    ? "Sign in and grant access — there's nothing else to fill in."
                    : "Give ChaiDojo the access it needs to read your data."}
              </DialogDescription>
            </>
          )}
        </DialogHeader>

        {waitingOnAuth ? (
          <div className="flex flex-col items-center gap-3 py-10 text-center">
            <Spinner className="size-6 text-muted-foreground" />
            <p className="text-sm font-medium text-foreground">
              Waiting for you to finish signing in…
            </p>
            <p className="max-w-[19rem] text-xs leading-relaxed text-muted-foreground">
              A {provider?.display_name} window is open. Close it if you'd rather not continue —
              nothing is saved until you approve.
            </p>
          </div>
        ) : step === "pick" ? (
          <div className="grid grid-cols-2 gap-2">
            {providers.map((p) => {
              // Name what the provider actually offers ("SharePoint"), not the
              // port it happens to feed — OneDrive and SharePoint are both files.
              const caps = p.surfaces?.length
                ? p.surfaces.map((s) => s.label)
                : p.ports.includes("file_source")
                  ? ["Object storage"]
                  : [];
              return (
                <button
                  key={p.id}
                  type="button"
                  onClick={() => pickProvider(p.id)}
                  className="flex flex-col items-start gap-2 rounded-lg border border-[var(--color-hairline)] bg-card p-3 text-left transition hover:border-[var(--color-brand-purple)] hover:bg-[var(--color-surface-soft)]"
                >
                  <ProviderIcon provider={p.id} className="size-6" />
                  <span className="text-sm font-medium leading-tight text-foreground">
                    {p.display_name}
                  </span>
                  {p.description && (
                    <span className="text-xs leading-snug text-muted-foreground">
                      {p.description}
                    </span>
                  )}
                  <div className="flex flex-wrap gap-1">
                    {caps.map((c) => (
                      <span
                        key={c}
                        className="rounded-full bg-[var(--color-surface-soft)] px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide text-muted-foreground"
                      >
                        {c}
                      </span>
                    ))}
                  </div>
                </button>
              );
            })}
          </div>
        ) : (
          <div className="flex flex-col gap-3">
            {/* Grant only what's needed: syncing a Drive folder is no reason to
                hand over a mailbox. Only what's ticked is ever requested. */}
            {surfaces.length > 0 && (
              <div className="flex flex-col gap-1.5">
                <Label>What should ChaiDojo be able to read?</Label>
                <div className="flex flex-col gap-1.5 rounded-lg border border-[var(--color-hairline)] p-3">
                  {surfaces.map((s) => (
                    <label
                      key={s.id}
                      className="flex cursor-pointer items-start gap-2.5"
                      htmlFor={`surface-${s.id}`}
                    >
                      <Checkbox
                        id={`surface-${s.id}`}
                        checked={chosen.includes(s.id)}
                        onCheckedChange={() => toggleSurface(s.id)}
                        className="mt-0.5"
                      />
                      <span className="min-w-0">
                        <span className="block text-sm leading-tight text-foreground">
                          {s.label}
                        </span>
                        {s.description && (
                          <span className="block text-xs leading-relaxed text-muted-foreground">
                            {s.description}
                          </span>
                        )}
                      </span>
                    </label>
                  ))}
                </div>
                {chosen.length === 0 && (
                  <p className="text-xs text-muted-foreground">
                    Pick at least one — there'd be nothing to sync otherwise.
                  </p>
                )}
              </div>
            )}

            {deploymentManaged ? (
              <div className="flex items-start gap-3 rounded-lg border border-[var(--color-hairline)] bg-[var(--color-surface-soft)] px-4 py-3">
                <ShieldCheck className="mt-0.5 size-4 shrink-0 text-[var(--color-brand-purple)]" />
                <p className="text-xs leading-relaxed text-muted-foreground">
                  A {provider?.display_name} window will open for you to sign in and approve access.
                  ChaiDojo never sees your password, and you can revoke access anytime from your{" "}
                  {provider?.display_name} account.
                </p>
              </div>
            ) : (
              <>
                {Object.entries(props).map(([key, schema]) => (
                  <div key={key} className="flex flex-col gap-1.5">
                    <Label htmlFor={`cfg-${key}`}>
                      {schema.title ?? key}
                      {!requiredKeys.includes(key) && (
                        <span className="text-muted-foreground"> (optional)</span>
                      )}
                    </Label>
                    <Input
                      id={`cfg-${key}`}
                      value={config[key] ?? ""}
                      onChange={(e) => setConfig((c) => ({ ...c, [key]: e.target.value }))}
                      placeholder={schema.description}
                    />
                  </div>
                ))}

                {authKind === "api_key" && (
                  <div className="flex flex-col gap-1.5">
                    <Label htmlFor="cred-api-key">API key</Label>
                    <Input
                      id="cred-api-key"
                      type="password"
                      value={credentials.api_key ?? ""}
                      onChange={(e) => setCredentials((c) => ({ ...c, api_key: e.target.value }))}
                    />
                  </div>
                )}

                {authKind === "basic" && (
                  <>
                    <div className="flex flex-col gap-1.5">
                      <Label htmlFor="cred-user">Username</Label>
                      <Input
                        id="cred-user"
                        value={credentials.username ?? ""}
                        onChange={(e) =>
                          setCredentials((c) => ({ ...c, username: e.target.value }))
                        }
                      />
                    </div>
                    <div className="flex flex-col gap-1.5">
                      <Label htmlFor="cred-pass">Password</Label>
                      <Input
                        id="cred-pass"
                        type="password"
                        value={credentials.password ?? ""}
                        onChange={(e) =>
                          setCredentials((c) => ({ ...c, password: e.target.value }))
                        }
                      />
                    </div>
                  </>
                )}
              </>
            )}

            <div className="flex flex-col gap-1.5">
              <Label htmlFor="conn-label">Label (optional)</Label>
              <Input
                id="conn-label"
                value={label}
                onChange={(e) => setLabel(e.target.value)}
                placeholder="e.g. Shared drive"
              />
            </div>
          </div>
        )}

        <DialogFooter>
          {waitingOnAuth ? (
            <Button variant="outline" onClick={() => setWaitingOnAuth(false)}>
              Cancel
            </Button>
          ) : (
            step === "configure" && (
              <>
                <Button variant="outline" onClick={() => setStep("pick")}>
                  Back
                </Button>
                <Button
                  variant="brand"
                  disabled={!canSubmit || submitting}
                  onClick={() => void submit()}
                >
                  {submitLabel}
                </Button>
              </>
            )
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
